import React, { useEffect, useState } from "react";
import { money, taxOn } from "@/lib/money";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus, Trash2, AlertCircle } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import QuotaChokeCard from "@/components/billing/QuotaChokeCard";
import api from "@/lib/api";
import { armFeedbackPrompt } from "@/lib/feedbackPrompt";
import { gateErrorText } from "@/lib/errors";
import { Button } from "@/ds";
import { useAuth } from "@/contexts/AuthContext";
import { taxAuthorityFor } from "@/lib/taxAuthorities";
import { backTarget } from "@/lib/cameFrom";
import DateField from "@/components/forms/DateField";

export default function SendQuote() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const jobId = params.get("job");
  // A confirmed phone is a signal, not a lock (Jorge, 2026-10-28): one quiet
  // line under the send button, and sending works the same either way.
  const phoneUnconfirmed = user?.profile?.phone_verified === false;
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [terms, setTerms] = useState("");
  const [extra, setExtra] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // T21-M5: a date field holding text it cannot read is not "no date".
  const [dateValid, setDateValid] = useState({ start: true, end: true });
  const [showDateErrors, setShowDateErrors] = useState(false);
  const [error, setError] = useState("");
  // The out-of-quotes card (PLANES M4c), filled from the 402 itself.
  const [choke, setChoke] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // N24. A string, not a number, so the box can be empty and mean "no tax"
  // rather than snapping to 0 while somebody is still typing "8.2".
  const [taxRate, setTaxRate] = useState("");
  const [myState, setMyState] = useState("");

  useEffect(() => {
    if (jobId) api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => setError(t("sendQuoteX.noJob")));
  }, [jobId]); // eslint-disable-line

  // Their saved default, offered rather than imposed: the box is pre-filled and
  // stays editable, because the rate on one quote is not a fact about them.
  useEffect(() => {
    api.get("/users/me").then((r) => {
      const profile = r.data?.profile || {};
      if (profile.default_tax_rate !== null && profile.default_tax_rate !== undefined) {
        setTaxRate(String(profile.default_tax_rate));
      }
      setMyState(profile.location?.state || "");
    }).catch(() => {});
  }, []);

  const subtotal = items.reduce((a, b) => a + (Number(b.quantity || 0) * Number(b.unit_price || 0)), 0);
  const rate = taxRate.trim() === "" ? null : Number(taxRate);
  // N34. The shared helper, so this form, Dany's card and the server are one
  // arithmetic rather than three that happen to agree today.
  const taxAmount = taxOn(rate, subtotal) ?? 0;
  const total = Math.round((subtotal + taxAmount) * 100) / 100;

  const setItem = (i, key, val) => {
    const next = [...items]; next[i] = { ...next[i], [key]: val };
    setItems(next);
  };

  const submit = async () => {
    if (!(dateValid.start && dateValid.end)) { setShowDateErrors(true); return; }
    setError("");
    setChoke(null);
    setSubmitting(true);
    try {
      await api.post(`/quotes`, {
        job_id: jobId,
        line_items: items.map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })),
        payment_terms: terms,
        additional_terms: extra,
        proposed_start_date: startDate || null,
        proposed_end_date: endDate || null,
        // null, not 0: an empty box means "this quote has no tax", and the
        // server stores that as NULL rather than as a rate of zero percent.
        tax_rate: rate !== null && Number.isFinite(rate) ? rate : null,
      });
      // T15. The FixyBee's moment, and the same arming-not-showing reason as
      // PostJob: this page replaces itself on success.
      armFeedbackPrompt();
      navigate("/provider/quotes", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "no_credits") {
        // Not an error (PLANES M4c): the two ways forward and the refill day,
        // all three from the refusal itself.
        setChoke({
          extraPrice: d.extra_price_usd ?? null, resetsAt: d.resets_at || null,
          extraMax: d.extra_max_per_purchase ?? null, canUpgrade: d.upgrade_available !== false,
        });
      } else {
        setError(gateErrorText(e, t, t("sendQuoteX.couldNotSend")));
      }
      setSubmitting(false);
    }
  };

  if (!job) return <DashboardLayout role="provider"><div className="skeleton h-40 w-full" /></DashboardLayout>;

  return (
    <DashboardLayout role="provider">
      {/* N1, same rule as the job page: an explicit destination, and never
          `navigate(-1)`. This form is reachable from a pasted link and from a
          "send a quote" CTA in an email, and both of those have nothing behind
          them — the blind version walked the FixyBee out of FixyDany. The
          fallback is the job this quote is for, which is where they were
          looking a moment ago in every path that does have a behind. */}
      <button onClick={() => navigate(backTarget(
                location, `/provider/jobs/${jobId}`))}
              className="fx-btn fx-btn--ghost mb-4" data-testid="back-btn">
        <ArrowLeft size={16} /> {t("common.back")}
      </button>

      <div data-testid="send-quote-page">
        <h1 className="font-display text-3xl font-bold text-worker">{t("sendQuoteX.title")}</h1>
        <p className="text-smoke mt-1">{t("sendQuoteX.forJob")} <span className="font-display font-semibold text-worker">{job.title}</span></p>

        {choke && (
          <div className="mt-6 max-w-3xl">
            <QuotaChokeCard extraPrice={choke.extraPrice} resetsAt={choke.resetsAt}
                            extraMax={choke.extraMax} canUpgrade={choke.canUpgrade}
                            testId="send-quote-no-quotes" />
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 rounded-sm bg-danger/10 border border-danger/30 text-danger flex gap-2 items-start text-sm" data-testid="send-quote-error">
            <AlertCircle size={18} /> {error}
          </div>
        )}

        <div className="mt-8 max-w-3xl space-y-5">
          <div>
            <label className="label-base">{t("sendQuoteX.lineItems")}</label>
            {/*
              One row of four columns fits a desktop and squeezes the
              description to ~165px on a phone, which is unreadable. Below sm
              the description takes the full width and quantity, unit price and
              delete share the line under it; the bottom rule keeps consecutive
              line items apart now that each one is two rows tall.
            */}
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 pb-3 border-b border-sand last:border-0 sm:pb-0 sm:border-0" data-testid={`line-${i}`}>
                  <input className="input-base col-span-12 sm:col-span-6" placeholder={t("sendQuoteX.lineDesc")} aria-label={t("sendQuoteX.lineDesc")} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
                  <input type="number" min="0" step="0.01" className="input-base col-span-5 sm:col-span-2" placeholder={t("sendQuoteX.lineQty")} aria-label={t("sendQuoteX.lineQty")} value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
                  <input type="number" min="0" step="0.01" className="input-base col-span-5 sm:col-span-3" placeholder={t("sendQuoteX.lineUnit")} aria-label={t("sendQuoteX.lineUnit")} value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                  <button onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="col-span-2 sm:col-span-1 fx-btn fx-btn--ghost px-2 disabled:opacity-30" disabled={items.length === 1} aria-label={t("common.delete")}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <button onClick={() => setItems([...items, { description: "", quantity: 1, unit_price: 0 }])} className="fx-btn fx-btn--secondary text-sm mt-2" data-testid="add-line">
              <Plus size={16} /> {t("sendQuoteX.addLine")}
            </button>
          </div>

          {/* N24. The rate is theirs and the link is the state's own office:
              FixyDany publishes no tax table, because a platform that
              pre-fills a rate has told a tradesperson what to charge the
              government. `taxAuthorityFor` never returns null, so this link is
              never dead — a state we do not name falls back to the Federation
              of Tax Administrators' directory. */}
          <div>
            <label className="label-base" htmlFor="quote-tax-rate">
              {t("sendQuoteX.taxRate")}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="quote-tax-rate"
                type="number" min="0" max="99.999" step="0.001"
                className="input-base max-w-[9rem]"
                placeholder={t("sendQuoteX.taxRatePh")}
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                data-testid="quote-tax-rate"
              />
              <span className="text-smoke">%</span>
            </div>
            <p className="text-xs text-smoke mt-1">
              <a href={taxAuthorityFor(myState)} target="_blank" rel="noopener noreferrer"
                 className="underline" data-testid="quote-tax-link">
                {t("sendQuoteX.taxLink")}
              </a>{" "}
              {t("sendQuoteX.taxHint")}
            </p>
          </div>

          <div className="bg-linen rounded-md p-4 space-y-1" data-testid="quote-total">
            <div className="flex justify-between items-center text-sm text-smoke">
              <span>{t("sendQuoteX.subtotal")}</span>
              <span className="font-mono">{money(subtotal, "$0.00")}</span>
            </div>
            {taxAmount > 0 && (
              <div className="flex justify-between items-center text-sm text-smoke" data-testid="quote-tax-amount">
                <span>{t("sendQuoteX.tax", { rate })}</span>
                <span className="font-mono">{money(taxAmount, "$0.00")}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-1">
              <span className="font-display font-bold text-worker">{t("sendQuoteX.total")}</span>
              <span className="font-mono text-2xl font-bold text-worker">{money(total, "$0.00")}</span>
            </div>
          </div>

          <div>
            <label className="label-base">{t("sendQuoteX.paymentTerms")}</label>
            <input className="input-base" placeholder={t("sendQuoteX.paymentTermsPh")} value={terms} onChange={(e) => setTerms(e.target.value)} data-testid="payment-terms" />
          </div>
          <div>
            <label className="label-base">{t("sendQuoteX.additionalTerms")}</label>
            <textarea className="input-base min-h-[80px]" placeholder={t("sendQuoteX.additionalTermsPh")} value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="additional-terms" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-base" htmlFor="quote-start-date">{t("sendQuoteX.proposedStart")}</label>
              <DateField id="quote-start-date" value={startDate} onChange={setStartDate}
                onValidityChange={(start) => setDateValid((v) => ({ ...v, start }))}
                showError={showDateErrors} data-testid="quote-start-date" />
            </div>
            <div>
              <label className="label-base" htmlFor="quote-end-date">{t("sendQuoteX.proposedEnd")}</label>
              <DateField id="quote-end-date" value={endDate} onChange={setEndDate}
                onValidityChange={(end) => setDateValid((v) => ({ ...v, end }))}
                showError={showDateErrors} data-testid="quote-end-date" />
            </div>
          </div>

          {/* The screen's one primary. */}
          <div className="flex flex-col items-stretch sm:items-end gap-2 pt-2">
            <Button onClick={submit} loading={submitting}
              disabled={total <= 0 || !items[0].description}
              className="w-full sm:w-auto" data-testid="submit-quote">
              {t("sendQuoteX.sendBtn", { amount: money(total, "$0.00").replace("$", "") })}
            </Button>
            {phoneUnconfirmed && (
              <p className="text-sm text-smoke sm:text-right" data-testid="send-quote-phone-line">
                {t("signupX.quotePhoneLine")}{" "}
                <Link to="/provider/verify-phone" className="font-semibold text-worker underline">
                  {t("signupX.quotePhoneLink")}
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
